import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import {
  supervisorAdd,
  supervisorGetById,
  supervisorOptionAllGet,
  supervisorUpdate
} from '../../../store/slices/tenantSlices/supervisorSlice';
import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import { didOptionGet } from '../../../store/slices/tenantSlices/incomingSlice';
import {
  callexAiAgentOptionsGet,
  outboundOptionGet,
  trunkOptionGet
} from '../../../store/slices/tenantSlices/configurationSlice';

const AddEditSupervisorModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [processOptions, setProcessOptions] = useState([]);
  const [supervisorRoleOptions, setSupervisorRoleOptions] = useState([]);
  const [didOptions, setDidOptions] = useState([]);
  const [outboundRouteOptions, setOutboundRouteOptions] = useState([]);
  const [trunkOptions, setTrunkOptions] = useState([]);
  const [aiAgentOptions, setAiAgentOptions] = useState([]);

  const initialValues = {
    username: '',
    password: '',
    superVisorRoleId: '',
    status: 'true',
    is_auditor: 'yes',
    process_ids: [],
    did_ids: [],
    outboundroute_ids: [],
    trunk_ids: [],
    ai_agent_ids: []
  };

  const validationSchema = Yup.object({
    username: Yup.string().required('username is required'),
    password: Yup.string().required('password is required'),
    superVisorRoleId: Yup.string().required('supervisor role is required'),
    process_ids: Yup.array().min(1, 'process ID is required').required('process ID is required')
  });

  // Only the documented fields. The four assignment arrays are always sent: PUT replaces each
  // one it receives, so an emptied dropdown has to reach the API as [] to clear it.
  const getPayload = () => ({
    username: values.username,
    password: values.password,
    supervisor_role_id: values.superVisorRoleId,
    // The previous API validated the role under this name; kept until every server has the new one.
    superVisorRoleId: values.superVisorRoleId,
    status: String(values.status) === 'true',
    is_auditor: values.is_auditor,
    process_ids: values.process_ids,
    did_ids: values.did_ids,
    outboundroute_ids: values.outboundroute_ids,
    trunk_ids: values.trunk_ids,
    ai_agent_ids: values.ai_agent_ids
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...getPayload(), id: editRecord.id };
        const response = await dispatch(supervisorUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = getPayload();
        const response = await dispatch(supervisorAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchSupervisorById = async () => {
    try {
      const response = await dispatch(supervisorGetById(editRecord?.id)).unwrap();
      const {
        supervisor = {},
        supervisorProcess = [],
        dids = [],
        outboundroutes = [],
        trunks = [],
        ai_agent = []
      } = response?.data || {};
      const toIds = (rows) => rows.map((x) => x.id);
      setValues({
        username: supervisor.username || '',
        password: supervisor.password || '',
        superVisorRoleId: supervisor.supervisor_role_id || '',
        status: String(supervisor.status),
        is_auditor: String(supervisor.is_auditor || ''),
        process_ids: supervisorProcess.map((x) => x.process_id),
        did_ids: toIds(dids),
        outboundroute_ids: toIds(outboundroutes),
        trunk_ids: toIds(trunks),
        ai_agent_ids: toIds(ai_agent)
      });
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchSupervisorById();
    }
  }, [editRecord]);

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getSupervisorOption = async () => {
    try {
      const response = await dispatch(supervisorOptionAllGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setSupervisorRoleOptions(options);
      } else {
        setSupervisorRoleOptions([]);
      }
    } catch (error) {
      setSupervisorRoleOptions([]);
      console.log('error', error);
    }
  };

  // DID, outbound route, trunk and AI agent lists are optional assignments; a list that fails
  // to load just leaves its dropdown empty.
  const getAssignOptions = async (optionThunk, labelKey, setOptions) => {
    try {
      const response = await dispatch(optionThunk()).unwrap();
      setOptions(
        (response?.data || []).map((val) => ({ label: val[labelKey] || '-', value: val.id }))
      );
    } catch (error) {
      setOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getProcessOption();
      getSupervisorOption();
      getAssignOptions(didOptionGet, 'number', setDidOptions);
      getAssignOptions(outboundOptionGet, 'name', setOutboundRouteOptions);
      getAssignOptions(trunkOptionGet, 'name', setTrunkOptions);
      getAssignOptions(callexAiAgentOptionsGet, 'name', setAiAgentOptions);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Supervisor' : 'Add Supervisor'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="supervisor" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="username"
                label="Username"
                placeholder="Enter Username"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="password"
                label="Password"
                placeholder="Enter Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="superVisorRoleId"
                label="Supervisor Role"
                placeholder="Select Supervisor Role"
                value={values.superVisorRoleId}
                options={supervisorRoleOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="process_ids"
                label="Process"
                placeholder="Select Process"
                value={values.process_ids}
                options={processOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isMulti
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="did_ids"
                label="DID"
                placeholder="Select DID"
                value={values.did_ids}
                options={didOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isMulti
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="outboundroute_ids"
                label="Outbound Route"
                placeholder="Select Outbound Route"
                value={values.outboundroute_ids}
                options={outboundRouteOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isMulti
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="trunk_ids"
                label="Trunk"
                placeholder="Select Trunk"
                value={values.trunk_ids}
                options={trunkOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isMulti
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="ai_agent_ids"
                label="AI Agent"
                placeholder="Select AI Agent"
                value={values.ai_agent_ids}
                options={aiAgentOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isMulti
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="is_auditor"
                label="Auditor"
                trueName="Yes"
                falseName="No"
                value={String(values.is_auditor === 'yes')}
                onChange={() =>
                  setFieldValue('is_auditor', values.is_auditor === 'yes' ? 'no' : 'yes')
                }
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="supervisor" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditSupervisorModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditSupervisorModal;
